import { AuthError } from 'firebase/auth';

export type FirebaseAuthErrorFlow = 'login' | 'register' | 'passwordReset' | 'google';

const WRONG_EMAIL_OR_PASSWORD = 'Wrong email or password.';

function isAuthError(e: unknown): e is AuthError {
  return (
    typeof e === 'object' &&
    e !== null &&
    'code' in e &&
    typeof (e as AuthError).code === 'string'
  );
}

export function getFirebaseAuthErrorMessage(
  error: unknown,
  flow: FirebaseAuthErrorFlow = 'login',
): string {
  if (!isAuthError(error)) {
    return 'Something went wrong. Please try again.';
  }
  const code = error.code;
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already in use. Try signing in or use a different email.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return WRONG_EMAIL_OR_PASSWORD;
    case 'auth/user-disabled':
      return 'Sign-in failed. Contact support if you need help.';
    case 'auth/user-not-found':
      return flow === 'passwordReset'
        ? 'We could not send a reset email. Check the address and try again.'
        : WRONG_EMAIL_OR_PASSWORD;
    case 'auth/weak-password':
      return 'Your password does not meet the requirements.';
    case 'auth/popup-closed-by-user':
      return 'The Google sign-in window was closed before finishing.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again when you are ready.';
    case 'auth/account-exists-with-different-credential':
      return 'This email is signed up with a different method. Try another sign-in option.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
